/**
 * BulkStatusModal — change status for many selected clients at once.
 *
 * Small confirm modal: pick a status, confirm, apply. This is also how the Inactive
 * filter view bulk-reactivates off-roster clients (choosing ACTIVE/ON_HOLD/DISCHARGED).
 */

import { useState } from 'react';
import styled from 'styled-components';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Select } from '../ui/Select';
import { useBulkUpdateClientStatus } from '../../hooks/useClients';
import { useCanSetClientInactive } from '../../hooks/useCanSetClientInactive';
import { extractApiError } from '../../utils/apiError';
import { clientStatusOptions } from '../../pages/ClientManagementPage/clientFormConstants';
import type { ClientStatus } from '../../types/clients';

interface BulkStatusModalProps {
  clientIds: string[];
  onClose: () => void;
  /** Called after a successful apply (parent clears selection + closes). */
  onDone: () => void;
}

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const Label = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Note = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0 0 ${({ theme }) => theme.spacing[4]};
`;

const ErrorText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.status.error.text};
  margin: 0;
`;

export function BulkStatusModal({ clientIds, onClose, onDone }: BulkStatusModalProps) {
  const [status, setStatus] = useState<ClientStatus>('ACTIVE');
  const [error, setError] = useState<string | null>(null);
  const mutation = useBulkUpdateClientStatus();
  const clientCount = clientIds.length;

  const statusOptions = clientStatusOptions(useCanSetClientInactive());

  const handleConfirm = () => {
    setError(null);
    mutation.mutate(
      { clientIds, status },
      {
        onSuccess: () => onDone(),
        onError: (err) => setError(extractApiError(err, 'Failed to update status.')),
      },
    );
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Update Status"
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" isLoading={mutation.isPending} onClick={handleConfirm}>
            Confirm
          </Button>
        </>
      }
    >
      <Note>
        {clientCount} client{clientCount === 1 ? '' : 's'} selected
      </Note>
      <Field>
        <Label>New status</Label>
        <Select
          options={statusOptions}
          value={status}
          onChange={(e) => setStatus(e.target.value as ClientStatus)}
          fullWidth
        />
      </Field>
      {error && <ErrorText>{error}</ErrorText>}
    </Modal>
  );
}

export default BulkStatusModal;
